import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import StarRating from './Questions/StarRating';
import RadioInputGroup from '../common/RadioInputGroup';
import TextAreaInput from '../common/TextAreaInput';
import { postRequest, putRequest } from '../../requestUtils';
import './Review.scss';

const instantiateRatings = (categories) => (
  categories.map((category) => ({ id: null, score: null, rating_category_id: category.id }))
);

const TEXT_QUESTIONS = [
  {
    number: 3,
    code: 'public_review',
    dist: 'Public Review',
    question: 'Share your experience working with',
    note: 'Note: This review will be shared with the consultant and visible on their profile.',
  },
  {
    number: 4,
    code: 'private_review',
    dist: 'Private Review',
    question: 'Share with Clora an internal feeback for',
    note: 'Note: This review will remain private and only shared internally with Clora.',
  },
];

const getAverageScore = (ratings) => ((
  ratings.reduce((prev, curr) => prev + curr.score, 0)) / ratings.length).toFixed(1);

export default class Review extends Component {
  constructor(props) {
    super(props);
    const { review } = props;
    this.state = {
      edit: props.edit,
      ratings: review.review_ratings.length > 0 ? review.review_ratings
        : instantiateRatings(review.categories),
      error: false,
      success: false,
      recommend: review ? review.recommend : true,
      publicReview: review ? review.public_review : '',
      privateReview: review ? review.private_review : '',
      published: review.public,
      publishedAt: review.published_at,
    };
    this.handleReviewSave = this.handleReviewSave.bind(this);
    this.handleUserInput = this.handleUserInput.bind(this);
    this.handleAPISync = this.handleAPISync.bind(this);
    this.handlePublishReview = this.handlePublishReview.bind(this);
  }

  handleAPISync(path, params, token, fxn, success, callback = () => {}) {
    fxn(path, params, token).then(() => {
      window.scrollTo(0, 0);
      this.setState({ error: false, success, edit: false });
      callback();
    }).catch((error) => {
      window.scrollTo(0, 0);
      this.setState({ error: error.message, success: false });
    });
  }

  handlePublishReview() {
    const { projectResourceId, review, csrfToken } = this.props;
    const { published } = this.state;

    this.handleAPISync(
      `/projects/${projectResourceId}/reviews/${review.resource_id}/publish`,
      { review: { public: !published } },
      csrfToken,
      putRequest,
      `Review ${published ? 'un' : ''}published.`,
      () => { this.setState({ published: !published, publishedAt: null }); },
    );
  }

  handleReviewSave() {
    const {
      ratings, recommend, publicReview, privateReview,
    } = this.state;
    const { review, projectResourceId, csrfToken } = this.props;
    const hasInvalidRatings = ratings.find(({ score }) => !score);

    if (hasInvalidRatings) {
      this.setState({
        success: false,
        error: "Please make sure you're rating all the categories.",
      }, () => window.scrollTo(0, 0));
      return;
    }

    const saveReview = {
      reviewee_id: review.reviewee_id,
      recommend,
      public_review: publicReview,
      private_review: privateReview,
      review_ratings_attributes: ratings.map((rating) => ({
        id: rating.id,
        rating_category_id: rating.rating_category_id,
        score: rating.score,
      })),
    };

    this.handleAPISync(
      review.resource_id ? `/projects/${projectResourceId}/reviews/${review.resource_id}` : `/projects/${projectResourceId}/reviews`,
      { review: saveReview },
      csrfToken,
      review.resource_id ? putRequest : postRequest,
      'Review saved.',
    );
  }

  handleUserInput(type, value, id) {
    const { ratings } = this.state;
    if (type === 'ratings') {
      ratings.find((r) => r.rating_category_id === id).score = value;
      this.setState({ [type]: ratings });
    } else {
      this.setState({ [type]: value });
    }
  }

  renderActionButton() {
    const { edit } = this.state;
    const { editable, review } = this.props;

    if (review.resource_id && edit && editable) {
      return (
        <Fragment>
          <button
            id="review-cancel-changes"
            className="btn btn-secondary-no-border bigger-button full-width btn-review"
            onClick={() => this.setState({ edit: false })}
          >
            Cancel Changes
          </button>
          <button
            id="review-save-changes"
            className="btn btn-primary bigger-button full-width btn-review"
            onClick={this.handleReviewSave}
          >
            Save Changes
          </button>
        </Fragment>
      );
    }

    if (edit) {
      return (
        <button
          id="review-save-feedback"
          className="btn btn-primary bigger-button full-width btn-review"
          onClick={this.handleReviewSave}
        >
          Save Feedback
        </button>
      );
    }

    return null;
  }

  renderActions() {
    const { isRep, review } = this.props;
    const { published } = this.state;

    if (review && (isRep || review.editable)) {
      return (
        <div className="g-row reviews-header  mb-3 ">
          { !isRep
            && (
            <div className="g-column g-column--centered">
              <div>
                You can edit this review until
                <strong className="ms-2 me-2">
                  {review.editable_deadline}
                </strong>
              </div>
            </div>
            )}
          <div className="g-column g-column--centered">
            <Button
              id="review-edit-review"
              variant="secondary"
              onClick={() => this.setState({ edit: true })}
            >
              Edit Review
            </Button>
          </div>
          <div className="g-column g-column--centered">
            { isRep && review.resource_id && (
            <Button
              id="review-publish-review"
              variant="primary ms-2"
              onClick={this.handlePublishReview}
            >
              { published ? 'Unpublish' : 'Publish' }
            </Button>
            )}
          </div>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      edit, error, success, ratings, recommend, published, publishedAt,
    } = this.state;
    const { review, editable } = this.props;

    return (
      <div className="reviews-container style-guide">
        <div className="g-row">
          {review.resource_id && editable && this.renderActions()}
        </div>
        <div className="g-row">
          <h2 className="reviews-intro normal-weight">
            Rate your experience with
            {' '}
            <strong>{review.consultant_name}</strong>
            {' '}
            for
            {' '}
            <strong>
              {review.project_name}
            </strong>
          </h2>
        </div>
        { publishedAt
          ? (
            <div className="g-row mt-3">
              This review was
              {' '}
              { published ? '' : 'un' }
              published on
              {' '}
              { publishedAt }
              .
            </div>
          ) : ''}
        <div className="g-row">
          {success && (
          <div className="success">
            <span>{success}</span>
          </div>
          )}
        </div>
        <div>
          <div className="reviews-question">
            <p className=" mb-3 ">
              {`1. How would you rate ${review.first_name}'s performance during this project in terms of:`}
              <span className="text-danger">*</span>
            </p>
            {review.categories.map(({ id, name }) => (
              <StarRating
                edit={edit}
                rating={ratings.find((r) => r.rating_category_id === id)}
                id={id}
                name={name}
                handleUserInput={this.handleUserInput}
              />
            ))}
            <div className="star-total">
              Average Score:
              {getAverageScore(ratings)}
            </div>
          </div>
          {error && (
          <div className="error">
            <i className="fas fa-exclamation-triangle" />
            <span>{error}</span>
          </div>
          )}

          <div className="reviews-question recommendation">
            <p className="body-text-1">
              2. Would you recommend
              {' '}
              {review.first_name}
              ?
              <span className="text-danger">*</span>
            </p>
            {edit
              ? (
                <RadioInputGroup
                  options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                  handleInputChange={(option) => this.handleUserInput('recommend', option.value)}
                  selected={recommend}
                  name="recommendation"
                  className="ml-40 mt-3"
                />
              )
              : <div className="ml-40 mt-3">{recommend ? 'Yes' : 'No'}</div>}
          </div>

          {
            TEXT_QUESTIONS.map((question) => (
              <div className="reviews-question recommendation">
                <p className="body-text-1  mb-3 ">
                  <p>
                    {question.number}
                    .
                    {' '}
                    <strong>{question.dist}</strong>
                    {` - ${question.question} ${review.first_name}`}
                    {' '}
                    <u>during this project.</u>
                  </p>
                  <div className="body-text-2 muted-gray-text">{question.note}</div>
                </p>
                {edit
                  ? (
                    <TextAreaInput
                      className={`form-control fs-hide ${question.code}`}
                      placeholder="Type your review here..."
                      textAreaPrompt=""
                      rows={3}
                      value={this.state[question.code]}
                      onAnswerChange={(v) => this.handleUserInput(question.code, v)}
                    />
                  )
                  : <div>{this.state[question.code]}</div>}
              </div>
            ))
}
          <div className="reviews-footer">
            {this.renderActionButton()}
          </div>
        </div>
      </div>
    );
  }
}

Review.propTypes = {
  edit: PropTypes.bool,
  editable: PropTypes.bool,
  review: PropTypes.shape({}),
  projectResourceId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  isRep: PropTypes.bool,
};

Review.defaultProps = {
  review: {
    review_ratings: [],
  },
  edit: false,
  editable: false,
  projectResourceId: null,
  isRep: false,
};
